import { useEffect, useRef, useState } from "react";

import { fetchCodexRuntimeReadiness, type CodexRuntimeReadiness } from "./codexRuntimeReadiness";

export interface CodexRuntimeReadinessTarget {
  readonly runtimeId: string;
  readonly organizationId: string;
}

function targetKey(target: CodexRuntimeReadinessTarget): string {
  return `${target.organizationId}:${target.runtimeId}`;
}

export function useCodexRuntimeReadiness(target: CodexRuntimeReadinessTarget | null): {
  readonly readiness: CodexRuntimeReadiness;
  readonly recheck: () => void;
  readonly isChecking: boolean;
} {
  const controllers = useRef(new Map<string, AbortController>());
  const previousSelectedKey = useRef<string | null>(null);
  const [readinessByTarget, setReadinessByTarget] = useState<
    Readonly<Record<string, CodexRuntimeReadiness>>
  >({});
  const selectedKey = target ? targetKey(target) : null;
  const readiness = selectedKey
    ? (readinessByTarget[selectedKey] ?? ({ status: "not_checked" } as const))
    : ({ status: "not_checked" } as const);

  useEffect(() => {
    const previousKey = previousSelectedKey.current;
    previousSelectedKey.current = selectedKey;
    if (!previousKey || previousKey === selectedKey) return;
    controllers.current.get(previousKey)?.abort();
    controllers.current.delete(previousKey);
    setReadinessByTarget((current) => {
      if (current[previousKey]?.status !== "checking") return current;
      const next = { ...current };
      delete next[previousKey];
      return next;
    });
  }, [selectedKey]);

  useEffect(() => {
    const activeControllers = controllers.current;
    return () => {
      for (const controller of activeControllers.values()) controller.abort();
      activeControllers.clear();
    };
  }, []);

  return {
    readiness,
    recheck: () => {
      if (target) {
        const key = targetKey(target);
        controllers.current.get(key)?.abort();
        const controller = new AbortController();
        controllers.current.set(key, controller);
        setReadinessByTarget((current) => ({
          ...current,
          [key]: { status: "checking" },
        }));
        void fetchCodexRuntimeReadiness({ ...target, signal: controller.signal }).then((result) => {
          if (controller.signal.aborted || controllers.current.get(key) !== controller) return;
          controllers.current.delete(key);
          setReadinessByTarget((current) => ({ ...current, [key]: result }));
        });
      }
    },
    isChecking: readiness.status === "checking",
  };
}
